import React, { useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { TrendingUp, DollarSign, CheckCircle2, Clock, FileText, Target, Calendar, Award } from 'lucide-react';

const money = (n) => (Number(n) || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

const ranges = [
  { key: 'month', daysFr: 'Ce mois-ci', daysEn: 'This month' },
  { key: 'quarter', daysFr: '3 mois', daysEn: '3 months' },
  { key: 'year', daysFr: 'Cette année', daysEn: 'This year' },
  { key: 'all', daysFr: 'Tout', daysEn: 'All time' },
];

function rangeStart(key) {
  const now = new Date();
  if (key === 'month') return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  if (key === 'quarter') return now.getTime() - 90 * 86400000;
  if (key === 'year') return new Date(now.getFullYear(), 0, 1).getTime();
  return 0;
}

export default function Stats() {
  const { submissions } = useData();
  const { lang } = useT();
  const [range, setRange] = useState('year');

  const stats = useMemo(() => {
    const start = rangeStart(range);
    const inRange = submissions.filter((s) => new Date(s.created_at || 0).getTime() >= start);

    const total = inRange.length;
    const accepted = inRange.filter((s) => s.status === 'accepted');
    const refused = inRange.filter((s) => s.status === 'refused');
    const pending = inRange.filter((s) => s.status === 'pending');
    const draft = inRange.filter((s) => s.status === 'draft');

    const submittedAmount = inRange
      .filter((s) => s.status !== 'draft')
      .reduce((sum, s) => sum + (Number(s.summary?.grandTotal) || 0), 0);

    const signedAmount = accepted.reduce((sum, s) => sum + (Number(s.summary?.grandTotal) || 0), 0);

    const decided = accepted.length + refused.length;
    const acceptanceRate = decided > 0 ? Math.round((accepted.length / decided) * 100) : 0;

    const avgTicket = accepted.length > 0 ? signedAmount / accepted.length : 0;

    // Real profit (signed - expenses)
    const totalExpenses = accepted.reduce((sum, s) => {
      const exp = (s.expenses || []).reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
      return sum + exp;
    }, 0);
    const realProfit = signedAmount - totalExpenses;
    const profitMargin = signedAmount > 0 ? Math.round((realProfit / signedAmount) * 100) : 0;

    // Top work types
    const byType = {};
    accepted.forEach((s) => {
      const wt = s.work_type || (lang === 'fr' ? 'Autre' : 'Other');
      byType[wt] = (byType[wt] || 0) + (Number(s.summary?.grandTotal) || 0);
    });
    const topTypes = Object.entries(byType)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      total,
      acceptedCount: accepted.length,
      refusedCount: refused.length,
      pendingCount: pending.length,
      draftCount: draft.length,
      submittedAmount,
      signedAmount,
      acceptanceRate,
      avgTicket,
      totalExpenses,
      realProfit,
      profitMargin,
      topTypes,
    };
  }, [submissions, range, lang]);

  return (
    <Layout title={lang === 'fr' ? 'Statistiques' : 'Statistics'}>
      <div className="mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">
          {lang === 'fr' ? 'Performance' : 'Performance'}
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter leading-[0.95]">
          {lang === 'fr' ? 'Vos chiffres.' : 'Your numbers.'}
        </h1>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-6">
        {ranges.map((r) => (
          <button
            key={r.key}
            data-testid={`stats-range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`min-h-[44px] rounded-lg border-2 text-[11px] font-bold transition-all ${
              range === r.key
                ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white'
                : 'border-[#E5DCC9] text-gray-600'
            }`}
          >
            {lang === 'fr' ? r.daysFr : r.daysEn}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        <BigCard
          label={lang === 'fr' ? 'Total signé' : 'Total signed'}
          value={money(stats.signedAmount)}
          accent="bg-[#7A1F2B] text-white"
          icon={Award}
          testid="big-signed"
        />

        <div className="grid grid-cols-2 gap-3">
          <Stat
            icon={FileText}
            label={lang === 'fr' ? 'Soumissions' : 'Submissions'}
            value={stats.total}
            testid="stats-total"
          />
          <Stat
            icon={CheckCircle2}
            label={lang === 'fr' ? 'Signées' : 'Signed'}
            value={stats.acceptedCount}
            color="text-emerald-700"
            testid="stats-signed-count"
          />
          <Stat
            icon={Target}
            label={lang === 'fr' ? 'Taux d\u2019acceptation' : 'Acceptance rate'}
            value={`${stats.acceptanceRate}%`}
            color="text-emerald-700"
            testid="stats-acceptance"
          />
          <Stat
            icon={DollarSign}
            label={lang === 'fr' ? 'Total soumissionné' : 'Total quoted'}
            value={money(stats.submittedAmount)}
            small
            testid="stats-submitted"
          />
          <Stat
            icon={Clock}
            label={lang === 'fr' ? 'En attente' : 'Pending'}
            value={stats.pendingCount}
            color="text-amber-700"
            testid="stats-pending"
          />
          <Stat
            icon={TrendingUp}
            label={lang === 'fr' ? 'Ticket moyen' : 'Avg ticket'}
            value={money(stats.avgTicket)}
            small
            testid="stats-avg"
          />
        </div>

        <div className="card p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">
            {lang === 'fr' ? 'Profit réel' : 'Real profit'}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">{lang === 'fr' ? 'Encaissé' : 'Cashed'}</div>
              <div className="font-heading font-black text-lg">{money(stats.signedAmount)}</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">{lang === 'fr' ? 'Dépenses' : 'Expenses'}</div>
              <div className="font-heading font-black text-lg text-amber-700">{money(stats.totalExpenses)}</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">{lang === 'fr' ? 'Profit net' : 'Net profit'}</div>
              <div className={`font-heading font-black text-lg ${stats.realProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {money(stats.realProfit)}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5DCC9]">
            <div className="text-xs text-gray-600">
              {lang === 'fr' ? 'Marge réelle' : 'Real margin'}: <b className={stats.profitMargin >= 15 ? 'text-emerald-700' : 'text-amber-700'}>{stats.profitMargin}%</b>
            </div>
          </div>
        </div>

        {stats.topTypes.length > 0 && (
          <div className="card p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">
              {lang === 'fr' ? 'Top types de travaux signés' : 'Top signed work types'}
            </div>
            <div className="space-y-2">
              {stats.topTypes.map(([type, amount], i) => {
                const max = stats.topTypes[0][1];
                const pct = max > 0 ? (amount / max) * 100 : 0;
                return (
                  <div key={type}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold truncate pr-2">{i + 1}. {type}</span>
                      <span className="font-heading font-black">{money(amount)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-[#7A1F2B]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value, color = 'text-[#7A1F2B]', small, testid }) {
  return (
    <div data-testid={testid} className="card p-4">
      <Icon className={`${color} mb-2`} size={18} strokeWidth={2.2} />
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading font-black tracking-tighter mt-1 ${small ? 'text-lg' : 'text-3xl'} ${color}`}>{value}</div>
    </div>
  );
}

function BigCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`card p-6 ${accent}`} style={{ background: '#7A1F2B', color: 'white' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</span>
        <Icon size={18} className="opacity-80" />
      </div>
      <div className="font-heading font-black text-4xl tracking-tighter">{value}</div>
    </div>
  );
}
